/**
 * `/templates/new` — naming a Template before authoring it.
 *
 * Deliberately small. Everything that makes a Template a Template — sections, items, options,
 * constraints — belongs to the authoring surface, and asking for it here would put a wizard in
 * front of a screen that is better at the job. This asks the two things that cannot be changed
 * casually afterwards and then gets out of the way.
 *
 * **Private by default.** Visibility is a deliberate act, never an omission: a template that becomes
 * public because somebody did not notice a dropdown is a parish's working notes on the open web.
 */

import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { TemplateVisibility } from '@pius/contracts';

import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import { createTemplate } from './api';
import './template-editor.css';

export function CreateTemplateScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const visibilities: { value: TemplateVisibility; label: string; note: string }[] = [
    {
      value: 'private',
      label: t('templates.vis_private_label'),
      note: t('templates.vis_private_note'),
    },
    {
      value: 'organization',
      label: t('templates.vis_org_label'),
      note: t('templates.vis_org_note'),
    },
    {
      value: 'public',
      label: t('templates.vis_public_label'),
      note: t('templates.vis_public_note'),
    },
  ];

  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<TemplateVisibility>('private');

  const create = useMutation({
    mutationFn: () => createTemplate({ name, visibility }),
    // Straight into authoring: naming a template is not the task, defining it is.
    onSuccess: async (created) => {
      await navigate({ to: '/templates/$id/edit', params: { id: created.id } });
    },
  });

  const error = create.error instanceof Error ? (create.error as ApiError) : undefined;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    create.mutate();
  };

  return (
    <div className="tpl-ed">
      <header className="tpl-ed__head">
        <div>
          <p className="tpl-ed__eyebrow">{t('templates.reusable_resource_eyebrow')}</p>
          <h1 className="tpl-ed__title">{t('templates.new_template')}</h1>
          <p className="tpl-ed__lede">{t('templates.create_lede')}</p>
        </div>
        <Link className="tpl-ed__link" to="/templates">
          {t('nav.templates')} →
        </Link>
      </header>

      <div className="tpl-ed__two">
        <form className="tpl-ed__main" onSubmit={submit}>
          <section className="tpl-ed__card">
            <label className="tpl-ed__field">
              <span className="tpl-ed__field-label">{t('templates.name_label')}</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Order of Celebrating Matrimony (Wedding Mass)"
                autoFocus
                required
              />
              <span className="tpl-ed__hint">{t('templates.name_hint')}</span>
            </label>

            <fieldset className="tpl-ed__field">
              <span className="tpl-ed__field-label">{t('templates.visibility_label')}</span>
              {visibilities.map((entry) => (
                <label key={entry.value} className="tpl-ed__radio">
                  <input
                    type="radio"
                    name="visibility"
                    checked={visibility === entry.value}
                    onChange={() => setVisibility(entry.value)}
                  />
                  <span>
                    <span className="tpl-ed__radio-label">{entry.label}</span>
                    <span className="tpl-ed__radio-note">{entry.note}</span>
                  </span>
                </label>
              ))}
              {/* The distinction people most often assume the other way round. */}
              <span className="tpl-ed__hint">{t('templates.create_vis_hint')}</span>
            </fieldset>

            {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

            <div className="tpl-ed__actions">
              <button
                type="submit"
                className="tpl-ed__btn"
                disabled={create.isPending || name.trim() === ''}
              >
                {create.isPending ? t('templates.creating') : t('templates.create_start_authoring')}
              </button>
            </div>
          </section>
        </form>

        <aside className="tpl-ed__side">
          <section className="tpl-ed__card tpl-ed__card--warm">
            <p className="tpl-ed__eyebrow">{t('templates.what_happens_next')}</p>
            <p className="tpl-ed__body">{t('templates.what_happens_next_body')}</p>
            <p className="tpl-ed__fine">{t('templates.what_happens_next_fine')}</p>
          </section>

          <section className="tpl-ed__card">
            <p className="tpl-ed__eyebrow">{t('templates.specialize_existing')}</p>
            <p className="tpl-ed__body">{t('templates.specialize_existing_body')}</p>
            <p className="tpl-ed__fine">{t('templates.specialize_existing_fine')}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}
